import {useFormikContext} from 'formik';
import {
    Alert,
    Autocomplete,
    Stack,
} from '@mantine/core';
import {AiOutlineInfoCircle} from 'react-icons/ai';

import {useRegisteredActionOptions} from '../hooks/useRegisteredActions';

type ActionCodeFieldProps = {
    name?: string,
    label?: string,
    description?: string,
};

// FNP-995. Offers the handler codes this deployment can actually perform.
//
// Replaces a required free-text TextInput. The actionCode is not a key an
// author invents — it is the name the code already gave itself (`sign-in.ts`
// declares `actionCode: 'user.sign-in'` about itself), so a catalog row only
// ever CLAIMS one that already exists in the deployment. Asking a human to type
// it was a console bug, not a schema decision.
//
// ⚠️ AUTOCOMPLETE, NOT SELECT — this is the load-bearing choice. FNP-859's rule
// is that an unregistered code is LEGAL: the executor fails open and records an
// `unimplemented` observation. That is what lets a test be authored BEFORE its
// handler exists, which is a demand signal sitting in the data. A locked Select
// would silently turn suggestions into a whitelist and destroy that.
//
// So an unknown code is WARNED about and still saves. Do not add a Yup
// `.oneOf(...)` against the registry, and do not disable submit on the warning.
export const ActionCodeField = ({
                                    name = 'actionCode',
                                    label = 'Action Code',
                                    description = 'The handle the executor dispatches on. Suggestions are the handlers this deployment can perform; an unlisted code is still legal and records an "unimplemented" observation.',
                                }: ActionCodeFieldProps) => {

    const {
        values,
        setFieldValue,
        errors,
        touched,
    } = useFormikContext<Record<string, unknown>>();

    const rawValue = values[name];
    const value = typeof rawValue === 'string' ? rawValue : '';

    const {
        options,
        selectedAction,
        isUnregistered,
        isError,
        registryAvailable,
    } = useRegisteredActionOptions(value);

    const fieldError = touched[name] && typeof errors[name] === 'string'
        ? errors[name] as string
        : undefined;

    return (
        <Stack gap={'xs'} my={'xl'}>
            <Autocomplete
                label={label}
                description={description}
                placeholder={'user.sign-in'}
                data={options.map(option => option.label)}
                value={value}
                withAsterisk
                error={fieldError}
                onChange={(next) => {
                    // The Autocomplete's data carries a decorated label
                    // ("user.sign-in (needs user.sign-up)"). Persist the CODE,
                    // never the label, or the row claims a handler that does
                    // not exist under that name.
                    const match = options.find(option => option.label === next);
                    void setFieldValue(name, match ? match.value : next);
                }}
            />

            {selectedAction && selectedAction.dependsOn.length > 0 && (
                <Alert
                    variant={'light'}
                    color={'blue'}
                    icon={<AiOutlineInfoCircle />}
                >
                    {`Depends on ${selectedAction.dependsOn.join(', ')} — the executor
                      skips this action when a dependency has not succeeded earlier in
                      the same wake. Declared by the handler in code; not editable here.`}
                </Alert>
            )}

            {isUnregistered && (
                // A WARNING, never a block. The row saves and the executor
                // records `unimplemented` — see the note at the top of this file.
                <Alert
                    variant={'light'}
                    color={'yellow'}
                    icon={<AiOutlineInfoCircle />}
                >
                    {`No handler named "${value}" is registered in this deployment.
                      That is legal — the action saves, and each wake records an
                      "unimplemented" observation until a handler ships.`}
                </Alert>
            )}

            {registryAvailable && isError && (
                <Alert
                    variant={'light'}
                    color={'gray'}
                    icon={<AiOutlineInfoCircle />}
                >
                    The handler registry could not be read, so there are no
                    suggestions. Any code you enter still saves.
                </Alert>
            )}
        </Stack>
    );
};
